import { Component, ViewEncapsulation, computed, effect, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MaterialModule } from 'src/app/material.module';
import { CoreService } from 'src/app/services/core.service';
import { RelatedArticleViewModel, WikiHelpService } from 'src/app/services/wiki-help.service';
import { KbChatService } from 'src/app/services/kb-chat.service';
import { ZohoImService } from 'src/app/services/zoho-im.service';
import { AskPanelChatComponent } from './ask-panel-chat/ask-panel-chat.component';

export type AskPanelTab = 'articles' | 'chat' | 'support';

@Component({
  selector: 'app-ask-panel',
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    TranslateModule,
    NgScrollbarModule,
    NgxSkeletonLoaderModule,
    AskPanelChatComponent,
  ],
  templateUrl: './ask-panel.component.html',
  styleUrl: './ask-panel.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AskPanelComponent {
  private readonly wikiHelpService = inject(WikiHelpService);
  private readonly kbChatService = inject(KbChatService);
  private readonly router = inject(Router);
  private readonly settings = inject(CoreService);
  private readonly zohoImService = inject(ZohoImService);

  opened = input(false);
  closePanel = output<void>();

  /**
   * The "Related articles" and "Ask AI" tabs are limited to companies in the Ask AI rollout.
   * `companyInRollout` is set by `HeaderComponent` once the backend rollout decision lands and stays
   * `false` until then, so a company outside the rollout never sees either tab — the panel itself
   * now opens for every company, because of the "Technical support" tab.
   */
  readonly articlesAvailable = computed(() => this.wikiHelpService.isConfigured && this.kbChatService.companyInRollout());
  readonly chatAvailable = computed(() => this.kbChatService.isConfigured && this.kbChatService.companyInRollout());
  /** The app wires Zoho's support widget (see `ZohoIm_CHANNEL`); not tied to the rollout. */
  readonly supportAvailable = this.zohoImService.isWired;

  /** Tabs shown, in order. With only one tab there is no tab to switch to, but its label still names the panel. */
  readonly tabs = computed<AskPanelTab[]>(() => {
    const tabs: AskPanelTab[] = [];
    if (this.articlesAvailable()) tabs.push('articles');
    if (this.chatAvailable()) tabs.push('chat');
    if (this.supportAvailable) tabs.push('support');
    return tabs;
  });

  /** The tab the user picked; the active tab falls back to the first one shown if it isn't available. */
  private readonly selectedTab = signal<AskPanelTab | null>(null);
  readonly activeTab = computed<AskPanelTab | null>(() => {
    const tabs = this.tabs();
    const selected = this.selectedTab();
    return selected && tabs.includes(selected) ? selected : (tabs[0] ?? null);
  });

  /** No widget script URL configured: the support tab shows only the unavailable message. */
  readonly supportConfigured = this.zohoImService.isConfigured;
  /** The last attempt to open Zoho's widget failed (backend switch off, or session/script load failure). */
  supportUnavailable = signal(false);
  supportOpening = signal(false);

  articles = signal<RelatedArticleViewModel[]>([]);
  isLoading = signal(false);
  hasError = signal(false);

  /** Route the currently held results belong to, so re-opening does not refetch needlessly. */
  private loadedRoute: string | null = null;

  constructor() {
    // Lazy: the first fetch happens when the panel is first opened, never at app boot.
    effect(() => {
      if (this.opened()) {
        // Only one of the panel and Zoho's widget is on screen at a time.
        this.zohoImService.close();
        this.loadRelatedArticles();
      }
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        if (this.opened()) {
          this.loadRelatedArticles();
        }
      });
  }

  selectTab(tab: AskPanelTab): void {
    this.selectedTab.set(tab);
    if (tab === 'articles' && this.opened()) {
      this.loadRelatedArticles();
    }
  }

  async openSupport(): Promise<void> {
    if (this.supportOpening()) {
      return;
    }
    this.supportOpening.set(true);
    try {
      // The panel closes as Zoho's widget appears, so the two never overlap.
      const result = await this.zohoImService.open(() => this.closePanel.emit());
      this.supportUnavailable.set(result === 'unavailable');
    } finally {
      this.supportOpening.set(false);
    }
  }

  get currentLanguage(): string {
    return this.settings.getLanguage() === 'ar' ? 'ar' : 'en';
  }

  getArticleLink(article: RelatedArticleViewModel): string {
    return this.wikiHelpService.getArticleLink(article, this.currentLanguage);
  }

  retry(): void {
    this.loadedRoute = null;
    this.loadRelatedArticles();
  }

  private loadRelatedArticles(): void {
    if (!this.articlesAvailable()) {
      return;
    }

    const route = this.wikiHelpService.buildRoute(this.router.url);

    if (route === this.loadedRoute && !this.hasError()) {
      return;
    }

    this.loadedRoute = route;
    this.isLoading.set(true);
    this.hasError.set(false);

    this.wikiHelpService.getRelated(route, this.currentLanguage).subscribe({
      next: (value) => {
        this.isLoading.set(false);
        this.articles.set(value?.results ?? []);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.hasError.set(true);
        this.articles.set([]);
        this.loadedRoute = null;
        console.error(err);
      },
    });
  }
}
